import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];
const WEEK_FR = ["L", "M", "M", "J", "V", "S", "D"];

/**
 * Mini-calendrier mensuel (B&W) qui pointille les dates contenant ≥1 événement.
 * Props:
 *  - marks: [{ date: "YYYY-MM-DD", event_id, event_name }, …]
 */
export default function MiniCalendar({ marks = [] }) {
  const today = new Date();
  const [view, setView] = useState({ y: today.getFullYear(), m: today.getMonth() });

  const marksByDate = useMemo(() => {
    const m = {};
    for (const x of marks) {
      (m[x.date] = m[x.date] || []).push(x);
    }
    return m;
  }, [marks]);

  // Build grid: monday-first
  const grid = useMemo(() => {
    const first = new Date(view.y, view.m, 1);
    const last = new Date(view.y, view.m + 1, 0);
    const offset = (first.getDay() + 6) % 7; // monday=0
    const days = [];
    for (let i = 0; i < offset; i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) {
      days.push(new Date(view.y, view.m, d));
    }
    while (days.length % 7 !== 0) days.push(null);
    return days;
  }, [view]);

  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const isToday = (d) => d && fmt(d) === fmt(today);

  const [hover, setHover] = useState(null); // {date, events:[…]}

  const move = (delta) => {
    let m = view.m + delta;
    let y = view.y;
    while (m < 0) { m += 12; y -= 1; }
    while (m > 11) { m -= 12; y += 1; }
    setView({ y, m });
  };

  return (
    <div className="card-flat" data-testid="mini-calendar">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label">Calendrier</div>
          <h3 className="font-serif text-2xl leading-tight tabular">
            {MONTHS_FR[view.m]} {view.y}
          </h3>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => move(-1)}
            className="p-1.5 border border-[var(--border-strong)] hover:border-black hover:bg-black hover:text-white transition-colors"
            aria-label="Mois précédent"
            data-testid="calendar-prev"
          >
            <ChevronLeft size={14} strokeWidth={2.5} />
          </button>
          <button
            onClick={() => setView({ y: today.getFullYear(), m: today.getMonth() })}
            className="px-2 py-1 font-mono text-[9px] uppercase tracking-widest border border-[var(--border-strong)] hover:border-black"
            data-testid="calendar-today"
          >
            Auj
          </button>
          <button
            onClick={() => move(1)}
            className="p-1.5 border border-[var(--border-strong)] hover:border-black hover:bg-black hover:text-white transition-colors"
            aria-label="Mois suivant"
            data-testid="calendar-next"
          >
            <ChevronRight size={14} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {WEEK_FR.map((w, i) => (
          <div key={i} className="font-mono text-[9px] uppercase tracking-widest text-[var(--muted)] text-center pb-1">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {grid.map((d, i) => {
          if (!d) return <div key={i} className="aspect-square" />;
          const iso = fmt(d);
          const events = marksByDate[iso] || [];
          const has = events.length > 0;
          const today = isToday(d);
          const cls = `aspect-square flex flex-col items-center justify-center text-xs tabular relative cursor-pointer transition-colors ${
            has ? "bg-black text-white hover:bg-[var(--surface-2)] hover:text-black" : "hover:bg-[var(--surface-3)]"
          } ${today ? "ring-2 ring-black ring-offset-1" : "border border-[var(--border)]"}`;
          return (
            <div
              key={i}
              className={cls}
              onMouseEnter={() => has && setHover({ date: iso, events })}
              onMouseLeave={() => setHover(null)}
              data-testid={`cal-day-${iso}`}
              data-has-events={has}
            >
              <span className={has ? "font-bold" : ""}>{d.getDate()}</span>
              {has && events.length > 1 && (
                <span className="font-mono text-[8px] mt-0.5">{events.length}</span>
              )}
            </div>
          );
        })}
      </div>

      {hover && hover.events.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[var(--border)]" data-testid="calendar-hover-info">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mb-1">{hover.date}</div>
          <div className="space-y-0.5">
            {hover.events.slice(0, 4).map((e, idx) => (
              <Link
                key={`${e.event_id}-${idx}`}
                to={`/evenements/${e.event_id}`}
                className="block text-xs hover:underline truncate"
              >
                → {e.event_name}
              </Link>
            ))}
            {hover.events.length > 4 && (
              <div className="font-mono text-[10px] text-[var(--muted)]">+ {hover.events.length - 4} autre{hover.events.length - 4 > 1 ? "s" : ""}</div>
            )}
          </div>
        </div>
      )}

      {!hover && marks.length > 0 && (
        <p className="mt-3 pt-3 border-t border-[var(--border)] font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
          Survole une date pour voir les événements.
        </p>
      )}
    </div>
  );
}
